import { useState, useEffect, useCallback, useRef } from "react";
import { env } from "@/lib/env";
import { PAGINATION } from "@/constants/constants";
import { usePageHeader } from "@/contexts/page-header-context";
import { Card, CardContent } from "@/components/_components/card";
import { Button } from "@/components/_components/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/_components/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/_components/badge";
import { Skeleton } from "@/components/_components/skeleton";
import { Search, RotateCcw, AlertCircle, Loader2, Briefcase, ChevronsLeft, ChevronsRight, ChevronLeft, ChevronRight } from "lucide-react";
import { usePaginatedQuery } from "@/hooks/usePaginatedQuery";
import * as api from "@/lib/api/client";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { PageAccessGuard } from "@/components/auth/PageAccessGuard";
import { formatDateTime } from "@/lib/utils";

interface JobOrderFilter {
  agentType: string;
  externalCd?: string;
  title?: string;
  countryCode?: string;
}

interface JobOrderData {
  id: number;
  externalCd: string;
  country: string;
  city: string;
  title: string;
  status: string;
  createdAt: string;
  lastResponeAt: string;
  recipientCount: number;
  recipientStatusCounts: number[];
}

const CandidateOutreachPage: React.FC = () => {
  return (
    <PageAccessGuard authority="CAMPAIGN_CANDIDATE_OUTREACH_VIEW">
      <CandidateOutreachContent />
    </PageAccessGuard>
  );
};

const CandidateOutreachContent: React.FC = () => {
  const { setHeaderData } = usePageHeader();
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    jobNo: "",
    role: "",
    country: "all",
  });
  const [pageSize, setPageSize] = useState(env.DEFAULT_PAGE_SIZE);
  const [statusColumns, setStatusColumns] = useState<string[]>([]);
  const currentFilterRef = useRef<JobOrderFilter>({
    agentType: "CANDIDATE_OUTREACH",
  });

  // Fetch countries from API
  const { data: countriesData } = useQuery({
    queryKey: ["countries"],
    queryFn: async () => {
      const response = await api.getCountriesList();
      return response.data || [];
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  const countries = countriesData || [];

  const paginatedQuery = usePaginatedQuery<JobOrderFilter, any>(
    (filter, options) => {
      return api.getTpmoTransMasters(filter, options);
    },
    { pageSize }
  );

  const {
    data: responseData,
    isPending: isLoading,
    error,
    executeQuery,
    currentPage,
    totalCount: total,
    setCurrentPage,
    currentFilter,
  } = paginatedQuery;

  // Extract job order data and status columns from response
  const jobOrderData = responseData?.data || [];
  const statusColumnsFromResponse = responseData?.status || [];

  const handleFilterChange = (field: string, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleSearch = useCallback(() => {
    const filter: JobOrderFilter = {
      agentType: "CANDIDATE_OUTREACH",
      ...(filters.jobNo && { externalCd: filters.jobNo }),
      ...(filters.role && { title: filters.role }),
      ...(filters.country && filters.country !== "all" && { countryCode: filters.country }),
    };
    currentFilterRef.current = filter;
    executeQuery(filter, 1);
  }, [filters, executeQuery]);

  const handleReset = useCallback(() => {
    setFilters({
      jobNo: "",
      role: "",
      country: "all",
    });
    const filter: JobOrderFilter = { agentType: "CANDIDATE_OUTREACH" };
    currentFilterRef.current = filter;
    executeQuery(filter, 1);
  }, [executeQuery]);

  // Update status columns when response data changes
  useEffect(() => {
    if (statusColumnsFromResponse && statusColumnsFromResponse.length > 0) {
      setStatusColumns(statusColumnsFromResponse);
    }
  }, [statusColumnsFromResponse]);

  const handleViewDetails = (id: number) => {
    navigate(`/candidate-outreach/details/${id}`);
  };

  const initialLoadRef = useRef(false);

  // Simple initial load on mount
  useEffect(() => {
    const performInitialLoad = () => {
      if (initialLoadRef.current) return;
      initialLoadRef.current = true;

      const filter: JobOrderFilter = { agentType: "CANDIDATE_OUTREACH" };
      currentFilterRef.current = filter;
      executeQuery(filter, 1);
    };

    // Small delay to ensure everything is ready
    const timer = setTimeout(performInitialLoad, 100);
    return () => clearTimeout(timer);
  }, []); // Empty dependency array

  useEffect(() => {
    setHeaderData({
      stats: responseData !== undefined ? {
        count: total || 0,
        label: 'job order',
        icon: <Briefcase className="h-4 w-4" />
      } : undefined,
    });
    return () => {
      setHeaderData(null);
    };
  }, [responseData, total, setHeaderData]);

  if (isLoading && !responseData) {
    return (
      <div className="bg-background">
        <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card className="border border-border/50 bg-card backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-background">
        <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card className="border border-destructive/30 bg-card backdrop-blur-sm">
            <CardContent className="p-12 text-center">
              <AlertCircle className="h-16 w-16 text-destructive mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">Error loading data</h3>
              <Button onClick={() => window.location.reload()} variant="outline">
                Try Again
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const tableData: JobOrderData[] = jobOrderData?.map((row: any, index: number) => {
    return {
      id: row.id || index + 1,
      externalCd: row.externalCd || "N/A",
      country: row.country || "N/A",
      city: row.city || "N/A",
      title: row.title || "N/A",
      status: row.status || "N/A",
      createdAt: row.createdAt || "",
      lastResponeAt: row.lastResponeAt || "",
      recipientCount: row.recipientCount || 0,
      recipientStatusCounts: row.recipientStatusCounts || [],
    };
  }) || [];

  return (
    <div className="bg-background">
      <div className="w-full mx-auto px-2 sm:px-2 lg:px-2 py-4">
        <div className="space-y-4">
          {/* Filters Section */}
          <div className="bg-card border border-border/50 rounded-lg">
            <div className="px-4 pt-2 pb-1 border-b border-border/50">
              <div className="flex items-center gap-2.5">
                <div className="p-1.5 bg-primary/10 rounded-md">
                  <Search className="h-4 w-4 text-primary" />
                </div>
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-semibold text-foreground leading-tight">
                    Search & Filter
                  </h3>
                  <span className="text-sm text-muted-foreground">•</span>
                  <p className="text-sm text-muted-foreground">
                    Find candidate outreach job orders
                  </p>
                </div>
              </div>
            </div>

            <div className="px-3 pb-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Input
                  placeholder="Job No"
                  value={filters.jobNo}
                  onChange={(e) => handleFilterChange("jobNo", e.target.value)}
                  className="h-9"
                />
                <Input
                  placeholder="Role"
                  value={filters.role}
                  onChange={(e) => handleFilterChange("role", e.target.value)}
                  className="h-9"
                />
                <Select value={filters.country} onValueChange={(value) => handleFilterChange("country", value)}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Country" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Countries</SelectItem>
                    {countries.map((country) => (
                      <SelectItem key={country.countryCode} value={country.countryCode}>
                        {country.countryCode} - {country.countryName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <Button onClick={handleSearch} disabled={isLoading} className="h-9">
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                      Searching...
                    </>
                  ) : (
                    <>
                      <Search className="h-4 w-4 mr-1.5" />
                      Search
                    </>
                  )}
                </Button>
                <Button onClick={handleReset} disabled={isLoading} variant="outline" className="h-9">
                  <RotateCcw className="h-4 w-4 mr-1" />
                  Reset
                </Button>
              </div>
            </div>
          </div>

          {/* Results Table */}
          <Card className="border border-border/50 bg-card backdrop-blur-sm">
            <CardContent className="p-4">
              <div className="flex items-center space-x-3 mb-6">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Briefcase className="h-5 w-5 text-primary" />
                </div>
                <div className="flex items-center gap-2.5 flex-wrap">
                  <h2 className="text-xl font-bold text-foreground">Recuritment Job Orders</h2>
                  <Badge variant="secondary" className="text-xs font-medium">
                    {total} {total === 1 ? 'record' : 'records'}
                  </Badge>
                </div>
              </div>

              <div className="overflow-hidden rounded-lg border border-border/50">
                <Table>
                  <TableHeader className="bg-muted/95">
                    <TableRow>
                      <TableHead>Sl No</TableHead>
                      <TableHead>Job Order No</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Total Recipients</TableHead>
                      {statusColumns.map((status) => (
                        <TableHead key={status}>{status}</TableHead>
                      ))}
                      <TableHead>Created At</TableHead>
                      <TableHead>Last Responded At</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoading ? (
                      Array.from({ length: 5 }).map((_, index) => (
                        <TableRow key={`skeleton-${index}`}>
                          {Array.from({ length: 5 + statusColumns.length }).map((_, colIndex) => (
                            <TableCell key={colIndex}>
                              <Skeleton className="h-4 w-full" />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : tableData.length > 0 ? (
                      tableData.map((row, index) => {
                        // Calculate serial number based on current page and index
                        const serialNumber = (currentPage - 1) * pageSize + index + 1;

                        return (
                          <TableRow key={row.id} className="hover:bg-muted/50">
                            <TableCell className="font-medium">{serialNumber}</TableCell>
                            <TableCell>
                              <button
                                onClick={() => handleViewDetails(row.id)}
                                className="text-primary hover:text-primary/80 hover:underline cursor-pointer transition-colors"
                                title="View Job Details"
                              >
                                {row.externalCd}
                              </button>
                            </TableCell>
                            <TableCell>{row.title}</TableCell>
                            <TableCell>{row.status}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="font-medium">
                                {row.recipientCount}
                              </Badge>
                            </TableCell>
                            {statusColumns.map((status, statusIndex) => (
                              <TableCell key={status}>
                                <Badge variant="secondary" className="font-medium">
                                  {row.recipientStatusCounts[statusIndex] || 0}
                                </Badge>
                              </TableCell>
                            ))}
                            <TableCell className="text-foreground">{formatDateTime(row.createdAt)}</TableCell>
                            <TableCell className="text-foreground">{formatDateTime(row.lastResponeAt)}</TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5 + statusColumns.length} className="h-24 text-center">
                          <div className="flex flex-col items-center justify-center gap-2">
                            <AlertCircle className="h-8 w-8 text-muted-foreground/50" />
                            <span>No results found</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between px-4 mt-4">
                <div className="hidden flex-1 text-sm text-muted-foreground lg:flex">
                  {total} {total === 1 ? 'record' : 'records'}
                </div>
                <div className="flex w-full items-center gap-8 lg:w-fit">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Rows per page</span>
                    <Select
                      value={pageSize.toString()}
                      onValueChange={(value) => {
                        const newSize = Number(value);
                        setPageSize(newSize);
                        if (currentFilter) {
                          executeQuery(currentFilter, 1, newSize);
                        } else {
                          setCurrentPage(1);
                        }
                      }}
                      disabled={isLoading}
                    >
                      <SelectTrigger className="h-8 w-[70px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PAGINATION.PAGE_SIZE_OPTIONS.map(size => (
                          <SelectItem key={size} value={size.toString()}>{size}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground whitespace-nowrap">
                      Page {currentPage} of {Math.ceil(total / pageSize) || 1}
                    </span>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1 || isLoading}
                        title="First page"
                      >
                        <ChevronsLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 1 || isLoading}
                        title="Previous page"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage >= Math.ceil(total / pageSize) || isLoading}
                        title="Next page"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => setCurrentPage(Math.ceil(total / pageSize))}
                        disabled={currentPage >= Math.ceil(total / pageSize) || isLoading}
                        title="Last page"
                      >
                        <ChevronsRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

    </div>
  );
}

export default CandidateOutreachPage;